import { getCartErrorMessage } from '@theme/cart-feedback';

class SProductForm extends HTMLElement {
  #ac;
  #editingItem;
  #editFailed = false;
  #editObserver;
  #abortController;
  #activeSubmitter;
  #activeSubmitterAriaDisabled;
  #activeSubmitterHadOpacityClass;
  #activeSubmitterHadCursorWaitClass;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };
    this.#loadCartEdit();

    this.addEventListener('submit', (event) => {
      if (!this.#shouldHandleSubmit(event)) return;

      event.preventDefault();
      if (this.getAttribute('aria-busy') === 'true') return;

      this.#submit(event.target, event.submitter);
    }, opts);
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#editObserver?.disconnect();
    this.#abortController?.abort();
    this.#setSubmitting(false);
  }

  #shouldHandleSubmit(event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return false;

    // If Standard Actions are unavailable, leave the Shopify product form native.
    if (!window.Shopify?.actions?.updateCart) return false;

    if (!this.#variantInput(form)) return false;

    const submitter = event.submitter;
    if (submitter instanceof HTMLElement && !submitter.matches('button[name="add"], input[name="add"]')) {
      return false;
    }

    return true;
  }

  async #submit(form, submitter) {
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    if (this.#editFailed || this.getAttribute("aria-busy") === "true") return
    const line = this.#lineFromFormData(new FormData(form));
    if (!line) {
      this.#abortController = null;
      return;
    }

    this.#setSubmitting(true, submitter);
    this.#showError('');

   try {
        const lines = [line];
        const feeLine = this.#extraColorFeeLine(form, line.quantity);
        if (feeLine) lines.push(feeLine);
        lines.push(...this.#pillowLetteringLines(form, line.quantity));
        if (lines.length > 1) {
          const group = crypto.randomUUID();
          for (const [index, entry] of lines.entries()) {
            entry.attributes = [...(entry.attributes || []), { key: '_fee_group', value: group }];
            if (index > 0) entry.attributes.push({ key: '_fee_units', value: String(entry.quantity / line.quantity) });
          }
        }
        if (line.sellingPlanId) {
          for (const extra of lines.slice(1)) extra.sellingPlanId = line.sellingPlanId;
        } 
        
        const options = {
          event: {
            context: 'product',
            detail: this.#eventDetail(),
          },
          signal,
        };
        await this.#prepareCartEdit(lines);
        let result = await window.Shopify.actions.updateCart({ lines }, options);
        // Depositly: a fee product without the deposit plan rejects the whole add.
        // Retry with the fees at full price so the item still reaches the cart.
        if (lines.length > 1 && line.sellingPlanId && this.#resultErrorMessage(result)) {
          for (const extra of lines.slice(1)) delete extra.sellingPlanId;
          result = await window.Shopify.actions.updateCart({ lines }, options);
        } 
        
        this.#throwIfAborted(signal);
        
        const errorMessage = this.#resultErrorMessage(result);
      if (errorMessage) {
        this.#showError(errorMessage);
      } else {
        if (this.#editingItem) location.assign(this.dataset.cartPage);
        else await window.Shopify.actions.openCart?.();
      }
    } catch (error) {
      if (signal.aborted) return;

      this.#showError(getCartErrorMessage(error) || this.dataset.errorMessage || '');
    } finally {
      if (!signal.aborted) {
        this.#setSubmitting(false);
        if (this.#abortController?.signal === signal) this.#abortController = null;
      }
    }
  }

  async #loadCartEdit() {
    const key = new URL(location.href).searchParams.get('cart_edit');
    if (!key) return;
    this.setAttribute('aria-busy', 'true');
    try {
      const response = await fetch(this.dataset.cartUrl);
      if (!response.ok) throw new Error(this.dataset.errorMessage);
      const cart = await response.json();
      const item = cart.items.find((entry) => entry.key === key && String(entry.product_id) === this.dataset.productId);
      if (!item) throw new Error(this.dataset.errorMessage);
      this.#editingItem = item;
      const form = this.querySelector('form');
      for (const control of form.elements) {
        const match = control.name?.match(/^properties\[(.+)]$/);
        if (match && Object.hasOwn(item.properties || {}, match[1]) && control.type !== 'file') {
          const value = String(item.properties[match[1]]);
          if (control.type === 'radio' || control.type === 'checkbox') control.checked = control.value === value;
          else control.value = value;
        }
        if (control.name === 'quantity') control.value = item.quantity;
        if (control.name === 'selling_plan') control.checked = String(control.value) === String(item.selling_plan_allocation?.selling_plan?.id || '');
      }
      for (const control of form.elements) {
        if (control.name?.startsWith('properties[')) control.dispatchEvent(new Event('change', { bubbles: true }));
      }
      this.#labelCartEdit();
      this.#editObserver = new MutationObserver(() => this.#labelCartEdit());
      this.#editObserver.observe(this, { childList: true, subtree: true });
    } catch (error) {
      this.#editFailed = true;
      this.#showError(error.message || this.dataset.errorMessage);
    } finally {
      this.removeAttribute('aria-busy');
    }
  }

  #labelCartEdit() {
    const button = this.querySelector('[name="add"]');
    if (button && button.textContent !== this.dataset.saveLabel) button.textContent = this.dataset.saveLabel;
    // Accelerated checkout would skip saving the edited line.
    this.querySelectorAll('.shopify-payment-button').forEach((button) => { button.hidden = true; });
  }

  async #prepareCartEdit(lines) {
    if (!this.#editingItem) return;
    const response = await fetch(this.dataset.cartUrl);
    if (!response.ok) throw new Error(this.dataset.errorMessage);
    const cart = await response.json();
    const current = cart.items.find((item) => item.key === this.#editingItem.key);
    if (!current) throw new Error(this.dataset.errorMessage);
    const group = current.properties?._fee_group;
    const oldFees = group ? cart.items.filter((item) => item.properties?._fee_group === group && item.properties?._fee_units) : [];
    const parent = lines[0];
    // Preserve upload URLs and app metadata not represented by editable controls.
    const editableKeys = new Set([...this.querySelectorAll('[name]')].filter((control) => !control.disabled && control.type !== 'file').map((control) => control.name.match(/^properties\[(.+)]$/)?.[1]).filter(Boolean));
    const attributes = new Map(Object.entries(current.properties || {}).filter(([key]) => !key.startsWith('_fee_') && !editableKeys.has(key)));
    for (const entry of parent.attributes || []) attributes.set(entry.key, entry.value);
    parent.attributes = [...attributes].map(([key, value]) => ({ key, value: String(value) }));
    parent.id = current.key;
    if (!parent.sellingPlanId) parent.sellingPlanId = null;
    lines.push(...oldFees.map((item) => ({ id: item.key, quantity: 0 })));
  }
  #variantInput(form) {
    const input = form.elements.namedItem('id');
    return input instanceof HTMLInputElement ? input : null;
  }

  #lineFromFormData(formData) {
    const variantId = String(formData.get('id') || '').trim();
    if (!variantId) return null;

    const quantityValue = Number.parseInt(String(formData.get('quantity') || '1'), 10);
    const quantity = Number.isFinite(quantityValue) && quantityValue > 0 ? quantityValue : 1;
    const line = {
      merchandiseId: variantId,
      quantity,
    };

    const sellingPlanId = String(formData.get('selling_plan') || '').trim();
    if (sellingPlanId) line.sellingPlanId = sellingPlanId;

    const attributes = this.#lineAttributes(formData);
    if (attributes.length) line.attributes = attributes;

    return line;
  }

  // Adds pillow lettering fee items: $1 per extra letter and the both-sides charge.
  #pillowLetteringLines(form, quantity) {
    const box = form.querySelector('[data-pillow-lettering]');
    if (!box) return [];

    const lines = [];
    const attributes = box.dataset.feeFor ? [{ key: 'For', value: box.dataset.feeFor }] : undefined;
    const extra = Number.parseInt(box.querySelector('[data-extra-letters]')?.value || '0', 10);
    if (box.dataset.letterFeeVariant && Number.isFinite(extra) && extra > 0) {
      const line = { merchandiseId: box.dataset.letterFeeVariant, quantity: extra * quantity };
      if (attributes) line.attributes = attributes;
      lines.push(line);
    }
    const sides = box.querySelector('[data-lettering-sides]')?.value || '';
    if (box.dataset.sidesFeeVariant && sides === 'Both sides') {
      const line = { merchandiseId: box.dataset.sidesFeeVariant, quantity };
      if (attributes) line.attributes = attributes;
      lines.push(line);
    }
    return lines;
  }

  // Adds the extra color fee item when the buyer picks more colors than the free limit.
  #extraColorFeeLine(form, quantity) {
    const picker = form.querySelector('[data-color-personalization][data-extra-color-fee-variant]');
    if (!picker) return null;

    const freeColors = Number.parseInt(picker.dataset.freeColors || '5', 10);
    const chosen = [...picker.querySelectorAll('select')].filter((select) => select.value.trim() !== '').length;
    if (!Number.isFinite(freeColors) || chosen <= freeColors) return null;

    const feeLine = { merchandiseId: picker.dataset.extraColorFeeVariant, quantity };
    if (picker.dataset.feeLabel && picker.dataset.feeFor) {
      feeLine.attributes = [{ key: picker.dataset.feeLabel, value: picker.dataset.feeFor }];
    }
    return feeLine;
  }

  #lineAttributes(formData) {
    const attributes = [];

    for (const [name, value] of formData) {
      const match = String(name).match(/^properties\[(.+)]$/);
      if (!match) continue;
      if (typeof File !== 'undefined' && value instanceof File) continue;

      const attributeValue = String(value).trim();
      if (!attributeValue) continue;

      attributes.push({ key: match[1], value: attributeValue });
    }

    return attributes;
  }

  #eventDetail() {
    const detail = { source: 's-product-form' };
    if (this.dataset.productId) detail.productId = this.dataset.productId;
    return detail;
  }

  #resultErrorMessage(result) {
    return result?.userErrors?.[0]?.message || '';
  }

  #showError(message) {
    const error = this.querySelector('[data-add-to-cart-error]');
    if (!error) return;

    error.textContent = message || '';
    error.hidden = !message;
  }

  #setSubmitting(submitting, submitter = this.#activeSubmitter) {
    if (submitting) {
      this.setAttribute('aria-busy', 'true');
      if (submitter instanceof HTMLElement) {
        this.#activeSubmitter = submitter;
        this.#activeSubmitterAriaDisabled = submitter.getAttribute('aria-disabled');
        this.#activeSubmitterHadOpacityClass = submitter.classList.contains('opacity-50');
        this.#activeSubmitterHadCursorWaitClass = submitter.classList.contains('cursor-wait');
        submitter.setAttribute('aria-disabled', 'true');
        submitter.classList.add('opacity-50', 'cursor-wait');
      }
      return;
    }

    this.removeAttribute('aria-busy');
    if (this.#activeSubmitter instanceof HTMLElement) {
      if (this.#activeSubmitterAriaDisabled == null) {
        this.#activeSubmitter.removeAttribute('aria-disabled');
      } else {
        this.#activeSubmitter.setAttribute('aria-disabled', this.#activeSubmitterAriaDisabled);
      }
      if (!this.#activeSubmitterHadOpacityClass) this.#activeSubmitter.classList.remove('opacity-50');
      if (!this.#activeSubmitterHadCursorWaitClass) this.#activeSubmitter.classList.remove('cursor-wait');
    }
    this.#activeSubmitter = null;
    this.#activeSubmitterAriaDisabled = null;
    this.#activeSubmitterHadOpacityClass = null;
    this.#activeSubmitterHadCursorWaitClass = null;
  }

  #throwIfAborted(signal) {
    if (!signal.aborted) return;

    if (typeof signal.throwIfAborted === 'function') {
      signal.throwIfAborted();
    }

    throw new DOMException('Aborted', 'AbortError');
  }
}

if (!customElements.get('s-product-form')) {
  customElements.define('s-product-form', SProductForm);
}
